import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye, 
  MoreHorizontal,
  Star,
  Globe,
  FileText,
  Users,
  TrendingUp,
  ArrowLeft
} from 'lucide-react'
import { supabase, Service } from '@/lib/supabase'
import { ServiceEditor } from '@/components/services/ServiceEditor'
import { ContentPreview } from '@/components/ui/content-preview'

// Database operations
const fetchServices = async (): Promise<Service[]> => {
  const { data, error } = await supabase
    .from('services')
    .select('*')
    .order('updated_at', { ascending: false });
  
  if (error) throw error;
  return data || [];
};

const updateServiceStatus = async (id: string, status: string): Promise<void> => {
  const { error } = await supabase
    .from('services')
    .update({ status, updated_at: new Date().toISOString() })
    .eq('id', id);
  
  if (error) throw error;
};

const deleteService = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('services')
    .delete()
    .eq('id', id);
  
  if (error) throw error;
};

export default function ServicesManagement() {
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'published' | 'draft' | 'archived'>('all')
  const [currentView, setCurrentView] = useState<'list' | 'editor' | 'preview'>('list')
  const [selectedService, setSelectedService] = useState<Service | null>(null)
  const [previewService, setPreviewService] = useState<Service | null>(null)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)

  const queryClient = useQueryClient();

  // Fetch services from database
  const { data: services = [], isLoading, error } = useQuery({
    queryKey: ['services'],
    queryFn: fetchServices
  });

  // Update service status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => 
      updateServiceStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
    }
  });

  // Delete service mutation
  const deleteMutation = useMutation({
    mutationFn: deleteService,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
    }
  });

  const filteredServices = services.filter(service => {
    const matchesSearch = service.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         service.description.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || service.status === statusFilter
    return matchesSearch && matchesStatus
  })

  const getStatusColor = (status: Service['status']) => {
    switch (status) {
      case 'published': return 'bg-green-100 text-green-800'
      case 'draft': return 'bg-yellow-100 text-yellow-800'
      case 'archived': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this service? This action cannot be undone.')) {
      deleteMutation.mutate(id);
    }
  }

  const handleStatusChange = (id: string, newStatus: Service['status']) => {
    updateStatusMutation.mutate({ id, status: newStatus });
  }

  const handleNewService = () => {
    setSelectedService(null);
    setCurrentView('editor');
  }

  const handleEditService = (service: Service) => {
    setSelectedService(service);
    setCurrentView('editor');
  }

  const handleViewService = (service: Service) => {
    setPreviewService(service);
    setIsPreviewOpen(true);
  }

  const handleEditorSave = (service: Service) => {
    setCurrentView('list');
    setSelectedService(null);
  }

  const handleEditorCancel = () => {
    setCurrentView('list');
    setSelectedService(null);
  }

  const handleEditorPreview = (service: Service) => {
    setPreviewService(service);
    setIsPreviewOpen(true);
  }

  const stats = {
    total: services.length,
    published: services.filter(s => s.status === 'published').length,
    draft: services.filter(s => s.status === 'draft').length,
    featured: services.filter(s => s.is_featured).length
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Error Loading Services</h2>
          <p className="text-gray-600">Unable to load services data. Please try again later.</p>
        </div>
      </div>
    );
  }

  // Show editor view
  if (currentView === 'editor') {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={handleEditorCancel}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Services
          </Button>
        </div>
        <ServiceEditor
          service={selectedService || undefined}
          onSave={handleEditorSave}
          onCancel={handleEditorCancel}
          onPreview={handleEditorPreview}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Services Management</h1>
          <p className="text-muted-foreground">
            Manage your masonry services with SEO optimization and content management.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleNewService}>
            <Plus className="mr-2 h-4 w-4" />
            Add Service
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Services</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground">
              All service pages
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Published</CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.published}</div>
            <p className="text-xs text-muted-foreground">
              Live on website
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Draft</CardTitle>
            <Edit className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.draft}</div>
            <p className="text-xs text-muted-foreground">
              In development
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Featured</CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.featured}</div>
            <p className="text-xs text-muted-foreground">
              Homepage featured
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle>Service Directory</CardTitle>
          <CardDescription>
            Manage all your masonry services, content, and SEO settings.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-1 gap-2">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search services..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="px-3 py-2 border border-input rounded-md bg-background text-sm"
              >
                <option value="all">All Status</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <Filter className="mr-2 h-4 w-4" />
                More Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Services Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Service</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Featured</TableHead>
                <TableHead>Footer Service</TableHead>
                <TableHead>Reviews</TableHead>
                <TableHead>Gallery Images</TableHead>
                <TableHead>Last Updated</TableHead>
                <TableHead className="w-[50px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredServices.map((service) => (
                <TableRow key={service.id}>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="font-medium">{service.title}</div>
                      <div className="text-sm text-muted-foreground line-clamp-1">
                        {service.description}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        /{service.slug}
                      </div>
                      {service.hero_headline && (
                        <div className="text-xs text-blue-600 font-medium">
                          {service.hero_headline}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(service.status)}>
                      {service.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {service.is_featured ? (
                      <Star className="h-4 w-4 text-yellow-500 fill-current" />
                    ) : (
                      <Star className="h-4 w-4 text-muted-foreground" />
                    )}
                  </TableCell>
                  <TableCell>
                    {service.is_footer_service ? (
                      <Badge variant="secondary" className="text-xs">
                        Footer
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground text-sm">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {service.review_ids ? service.review_ids.length : 0} reviews
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {service.gallery_images ? service.gallery_images.length : 0} images
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-muted-foreground">
                      {new Date(service.updated_at).toLocaleDateString()}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleViewService(service)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEditService(service)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 text-destructive"
                        onClick={() => handleDelete(service.id)}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Quick Actions Panel */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>
            Common service management tasks and shortcuts.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Button variant="outline" className="h-auto p-4 justify-start" onClick={handleNewService}>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100 text-blue-600">
                  <Plus className="h-4 w-4" />
                </div>
                <div className="text-left">
                  <div className="font-medium">Add Service</div>
                  <div className="text-sm text-muted-foreground">Create new service page</div>
                </div>
              </div>
            </Button>

            <Button variant="outline" className="h-auto p-4 justify-start">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100 text-green-600">
                  <TrendingUp className="h-4 w-4" />
                </div>
                <div className="text-left">
                  <div className="font-medium">SEO Audit</div>
                  <div className="text-sm text-muted-foreground">Review SEO performance</div>
                </div>
              </div>
            </Button>

            <Button variant="outline" className="h-auto p-4 justify-start">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-100 text-purple-600">
                  <Users className="h-4 w-4" />
                </div>
                <div className="text-left">
                  <div className="font-medium">Service Areas</div>
                  <div className="text-sm text-muted-foreground">Manage coverage areas</div>
                </div>
              </div>
            </Button>

            <Button variant="outline" className="h-auto p-4 justify-start">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                  <FileText className="h-4 w-4" />
                </div>
                <div className="text-left">
                  <div className="font-medium">Content Review</div>
                  <div className="text-sm text-muted-foreground">Review all content</div>
                </div>
              </div>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview Dialog */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="max-w-5xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {previewService?.title || 'Service Preview'}
            </DialogTitle>
          </DialogHeader>
          {previewService && (
            <div className="space-y-6">
              {/* Service Hero */}
              <div className="border-b pb-6">
                {previewService.hero_image && (
                  <div className="aspect-video rounded-lg overflow-hidden mb-4">
                    <img 
                      src={previewService.hero_image} 
                      alt={previewService.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                
                <div className="space-y-4">
                  {previewService.hero_headline && (
                    <h2 className="text-2xl font-bold text-blue-600">
                      {previewService.hero_headline}
                    </h2>
                  )}
                  
                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                    <Badge className={getStatusColor(previewService.status)}>
                      {previewService.status}
                    </Badge>
                    {previewService.is_featured && (
                      <Badge variant="outline" className="text-yellow-600">
                        <Star className="w-3 h-3 mr-1" />
                        Featured
                      </Badge>
                    )}
                    {previewService.is_footer_service && (
                      <Badge variant="outline">Footer Service</Badge>
                    )}
                    {previewService.category && (
                      <Badge variant="secondary">{previewService.category}</Badge>
                    )}
                  </div>
                  
                  <p className="text-lg text-muted-foreground">
                    {previewService.description}
                  </p>
                </div>
              </div>

              {/* Service Content */}
              <div className="space-y-6">
                <ContentPreview 
                  content={previewService.content}
                  showBorder={false}
                  maxHeight="none"
                  className="prose-lg"
                />

                {/* Benefits */}
                {previewService.benefits && previewService.benefits.length > 0 && (
                  <div>
                    <h3 className="text-xl font-semibold mb-4">Service Benefits</h3>
                    <div className="grid gap-4 md:grid-cols-2">
                      {previewService.benefits.map((benefit) => (
                        <div key={benefit.id} className="p-4 border rounded-lg">
                          <h4 className="font-medium mb-2">{benefit.title}</h4>
                          <p className="text-sm text-muted-foreground">{benefit.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Process Steps */}
                {previewService.process_steps && previewService.process_steps.length > 0 && (
                  <div>
                    <h3 className="text-xl font-semibold mb-4">Our Process</h3>
                    <div className="space-y-4">
                      {previewService.process_steps.map((step) => (
                        <div key={step.id} className="flex gap-4">
                          <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium">
                            {step.step_number}
                          </div>
                          <div>
                            <h4 className="font-medium">{step.title}</h4>
                            <p className="text-sm text-muted-foreground">{step.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* FAQs */}
                {previewService.faqs && previewService.faqs.length > 0 && (
                  <div>
                    <h3 className="text-xl font-semibold mb-4">Frequently Asked Questions</h3>
                    <div className="space-y-4">
                      {previewService.faqs.map((faq) => (
                        <div key={faq.id} className="border-l-2 border-blue-200 pl-4">
                          <h4 className="font-medium mb-2">{faq.question}</h4>
                          <p className="text-sm text-muted-foreground">{faq.answer}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* CTA */}
                {previewService.cta_text && (
                  <div className="text-center py-6 border-t">
                    <Button size="lg">
                      {previewService.cta_text}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}